// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// AuthCallback — receives the Emergent OAuth fragment `#session_id=<token>`,
// exchanges it for a Bella Casa JWT, persists user+token in localStorage so
// the existing axios bearer flow continues to work, then redirects to the
// role-appropriate dashboard.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

function resolveRoleHome(role) {
  // The app's "/" route is a RoleBasedDashboard wrapped in ProtectedRoute,
  // so we always send the user there and let it pick the right view.
  return '/';
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [message, setMessage] = useState('Finalising sign-in…');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setMessage('Missing session_id — redirecting to login…');
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google-session`, { session_id: sessionId });
        const { token, user } = res.data || {};
        if (!token || !user) throw new Error('Invalid response from server');
        // Persist using the same keys as the email/password login path.
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        toast.success(`Welcome, ${user.name || user.email}`);
        // Clean up the fragment before navigating so we don't loop.
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate(resolveRoleHome(user.role), { replace: true });
      } catch (e) {
        // iter-318b: 409 ACCOUNT_NOT_FOUND → route to /auth/google-signup with the
        // short-lived google_token so the user can pick a role.
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 409 && detail && detail.code === 'ACCOUNT_NOT_FOUND') {
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/auth/google-signup', {
            replace: true,
            state: {
              google_token: detail.google_token,
              email: detail.email,
              name: detail.name,
              picture: detail.picture,
            },
          });
          return;
        }
        const msg = (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Sign-in failed';
        toast.error(msg);
        setMessage(msg);
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="auth-callback">
      <div className="bg-white rounded-xl shadow-md p-8 flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-stone-600 text-sm">{message}</p>
      </div>
    </div>
  );
}
